import {useEffect,useState} from 'react';
import {Download,PlayCircle,RefreshCw} from 'lucide-react';
import {supabase} from '../lib/supabase';
import {technicalSkillOperations} from '../data/TechnicalSkillOperations';
import {oddsAnalysis,analyzeData,investigateRecords,signalReview} from '../../../supabase/functions/agentopia-skill-capability-v1/analytics';
import {canvasDesign,createMcpServer,createSkillPackage,createWebApp,staticWebTest} from '../../../supabase/functions/agentopia-skill-capability-v1/creators';
import {csvDecode} from '../../../supabase/functions/agentopia-skill-capability-v1/sources';
import {scoreMarketBreadth,scoreMarketUptrend,normalizeFreePublicJson} from '../data/TechnicalMarketScoring';

type Inputs=Record<string,unknown>;
type Output=Record<string,unknown>;
type FileResult={name:string;mediaType:string;content:string};
type CallResult={ok?:boolean;error?:string;pending?:boolean;fetchId?:string;data?:unknown;output?:unknown;contentType?:string;source?:string};
const freeFetchOps=new Set([
 'company_search_live','github_public_search','market_health_live','market_uptrend_live',
 'market_ticker','market_orderbook','market_candles','market_trades','market_funding',
 'market_open_interest','sports_odds_live','seo_live','site_lighthouse','competitor_site_audit','seo_html_live'
]);
const kindByOperation:Record<string,string>={
 task_list:'task',task_add:'task',task_update:'task',task_complete:'task',task_delete:'task',
 thesis_save:'investment_thesis',thesis_list:'investment_thesis',thesis_close:'investment_thesis',
 thesis_report:'investment_thesis',source_list:'source',source_save:'source',source_remove:'source',
 save_document:'document',find_information:'document',
 company_save:'company',company_research:'company',
 competitor_save:'competitor',competitor_research:'competitor',
 customer_save:'customer',customer_research:'customer',
 deal_save:'deal',deal_review:'deal',pipeline_saved:'deal',signal_save:'signal',
 signal_review:'signal',signal_history:'signal',
 artifact_save:'artifact',artifact_list:'artifact'
};
const str=(v:unknown)=>String(v??'').trim();
const dict=(v:unknown):Inputs=>v&&typeof v==='object'&&!Array.isArray(v)?v as Inputs:{};
const rows=(v:unknown):Inputs[]=>Array.isArray(v)?v.map(dict):[];
function exportFile(file:FileResult){
 if(!file||typeof file.content!=='string')return;
 const blob=new Blob([file.content],{type:file.mediaType||'text/plain'});
 const uri=URL.createObjectURL(blob);
 const node=document.createElement('a');
 node.href=uri;node.download=file.name.split('/').at(-1)||'output.txt';
 document.body.appendChild(node);node.click();node.remove();
 window.setTimeout(()=>URL.revokeObjectURL(uri),1200);
}
async function api(name:string,args:Inputs){
 const {data,error}=await supabase.rpc(name,args);
 if(error)throw new Error(error.message);
 return data as unknown;
}
async function recordApi(action:string,kind:string,label:unknown=null,body:unknown=null,id:unknown=null){
 return dict(await api('agentopia_owner_skill_record_api',{
  p_action:action,p_kind:kind,p_label:label,p_body:body,p_id:id
 }));
}
function asRecords(data:unknown){
 return rows(dict(data).records);
}
function makeFiles(input:Inputs,op:string):Output{
 if(op==='canvas_generate')return canvasDesign(input);
 if(op==='mcp_generate')return createMcpServer(input);
 if(op==='skill_generate')return createSkillPackage(input);
 if(op==='webapp_generate')return createWebApp(input);
 if(op==='html_static_test'||op==='seo_html_static')return staticWebTest(input);
 if(op==='playwright_test_generate'){
  const url=str(input.url);
  if(!/^https:\/\/[a-z0-9.-]+(?:\/[a-zA-Z0-9/_-]*)?$/i.test(url))throw Error('Public HTTPS URL required');
  const code='import {test,expect} from "@playwright/test";\n'+
   'test("basic page health",async({page})=>{await page.goto('+JSON.stringify(url)+
   ');await expect(page.locator("body")).toBeVisible();'+
   'await expect(page).toHaveTitle(/.+/);});\n';
  return {files:[{name:'tests/site.spec.ts',mediaType:'text/plain',content:code}],
   note:'Run the generated Playwright test in an authorized browser test runner. No browser test was executed.'};
 }
 throw Error('Unknown local artifact operation');
}
async function perform(op:string,input:Inputs):Promise<Output>{
 if(['convert_odds','devig','find_edge','kelly_criterion','find_arbitrage',
  'parlay_analysis','line_movement','hedge_matrix'].includes(op))return oddsAnalysis(op,input);
 if(op==='data_analysis')return analyzeData(input);
 if(op==='signal_review'){
  const signals=Array.isArray(input.signals)?input.signals:
   asRecords(await recordApi('list','signal')).map(r=>dict(r.body));
  return signalReview({...input,signals});
 }
 if(op==='competitor_research'||op==='customer_research'||op==='company_research'||
  op==='deal_review'||op==='pipeline_review'||op==='pipeline_saved'||op==='digest_saved'||
  op==='find_information'){
  const kind=kindByOperation[op]||(op==='pipeline_review'?'deal':'event');
  const stored=op==='digest_saved'?'event':kind;
  const data=Array.isArray(input.records)?input.records:
   asRecords(await recordApi('list',stored)).map(r=>({...dict(r.body),id:r.id,label:r.label}));
  const mode=op==='pipeline_saved'?'pipeline_review':op==='digest_saved'?'digest':op;
  return investigateRecords(mode,{...input,records:data});
 }
 if(op==='analyze_saved'){
  const kind=str(input.recordType||'document');
  if(!['company','competitor','customer','deal','document','event','source','signal'].includes(kind))
   throw Error('Unsupported data type.');
  const data=asRecords(await recordApi('list',kind)).map(r=>dict(r.body));
  return analyzeData({...input,rows:data});
 }
 if(op==='task_list'||op==='thesis_list'||op==='source_list'||op==='signal_history'||op==='artifact_list'){
  const kind=kindByOperation[op];return await recordApi('list',kind);
 }
 if(op==='thesis_report'){
  const list=asRecords(await recordApi('list','investment_thesis'));
  const statuses:Record<string,number>={};
  for(const item of list){const status=str(dict(item.body).status||'idea');statuses[status]=(statuses[status]||0)+1;}
  return {count:list.length,byStatus:statuses,theses:list,noOrdersPlaced:true};
 }
 if(['task_add','thesis_save','source_save','save_document','company_save','competitor_save',
   'customer_save','deal_save','signal_save','artifact_save'].includes(op)){
  const kind=kindByOperation[op],label=str(input.label||input.name||input.id);
  if(!label)throw Error('A record label is required.');
  return await recordApi('save',kind,label,input);
 }
 if(['task_delete','source_remove'].includes(op))return await recordApi('delete',kindByOperation[op],null,null,input.id);
 if(['task_complete','task_update','thesis_close'].includes(op)){
  const kind=kindByOperation[op],records=asRecords(await recordApi('list',kind));
  const old=records.find(x=>x.id===input.id);
  if(!old)throw Error('Record not found.');
  const changes=op==='task_complete'?{status:'done'}:op==='thesis_close'?
   {status:'closed',closedAt:new Date().toISOString(),closeNotes:str(input.notes).slice(0,600)}:
   dict(input.changes);
  if(op==='task_update'&&Object.keys(changes).some(k=>!['status','dueDate','priority','notes','assignee','title'].includes(k)))
   throw Error('Only task status, dates, priority, notes, assignee or title may be updated.');
  return await recordApi('save',kind,old.label,{...dict(old.body),...changes});
 }
 if(op==='digest_live'){
  const {data,error}=await supabase.from('agentopia_tasks')
   .select('id,title,status,updated_at').order('updated_at',{ascending:false}).limit(80);
  if(error)throw Error('Workspace activity is not accessible under owner permissions.');
  return investigateRecords('digest',{days:7,records:rows(data).map(x=>({...x,at:x.updated_at}))});
 }
 return makeFiles(input,op);
}
const isFile=(x:unknown):x is FileResult=>Boolean(x&&typeof x==='object'&&
  typeof (x as FileResult).name==='string'&&typeof (x as FileResult).content==='string');
export default function SkillCapabilityRunnerLive({skillId,available}:{skillId:string;available:boolean}){
 const ops=technicalSkillOperations[skillId]||[];
 const [selected,setSelected]=useState(ops[0]?.value||'');
 const [inputs,setInputs]=useState(JSON.stringify(ops[0]?.example||{},null,2));
 const [busy,setBusy]=useState(false);
 const [result,setResult]=useState<Output|null>(null);
 const [error,setError]=useState('');
 const [fetchId,setFetchId]=useState<string|null>(null);
 const [receipt,setReceipt]=useState('');
 useEffect(()=>{
  const op=ops[0];
  setSelected(op?.value||'');setInputs(JSON.stringify(op?.example||{},null,2));
  setResult(null);setError('');setFetchId(null);setReceipt('');
 },[skillId]);
 if(!ops.length)return null;
 async function run(){
  if(busy||!available)return;
  let data:Inputs;
  try{
   if(inputs.length>12000)throw Error('Input exceeds the 12,000-character limit.');
   const parsed:unknown=JSON.parse(inputs);
   if(!parsed||typeof parsed!=='object'||Array.isArray(parsed))throw Error('JSON input must be an object.');
   data=parsed as Inputs;
  }catch(e){setError(e instanceof Error?e.message:'Invalid JSON.');return;}
  setBusy(true);setError('');setResult(null);setFetchId(null);setReceipt('');
  try{
   if(freeFetchOps.has(selected)){
    const parameter=selected.startsWith('market_')&&!['market_health_live','market_uptrend_live'].includes(selected)
      ?str(data.instId||'BTC-USDT'):selected==='sports_odds_live'?str(data.league||'nba'):
       ['seo_live','site_lighthouse','competitor_site_audit','seo_html_live'].includes(selected)?str(data.url||''):
       str(data.query||'');
    const response=dict(await api('agentopia_owner_skill_public_fetch_start',{
     p_skill_id:skillId,p_operation:selected,p_parameter:parameter
    }));
    if(response.ok!==true||typeof response.fetchId!=='string')throw Error(str(response.error||'Source request rejected.'));
    setFetchId(response.fetchId);setReceipt(response.fetchId);
    setResult({status:'Requested current public data.',source:response.origin,fetchId:response.fetchId,
     nextStep:'Select Check source result to retrieve the finished response.'});
   }else{
    const before=await crypto.subtle.digest('SHA-256',
      new TextEncoder().encode(JSON.stringify({selected,data})));
    const hash=[...new Uint8Array(before)].map(x=>x.toString(16).padStart(2,'0')).join('');
    const runId=await api('agentopia_owner_begin_skill_capability',{
     p_skill_id:skillId,p_operation:selected,p_input_sha256:hash
    });
    if(typeof runId!=='string')throw Error('The employee or skill usage limit was reached.');
    setReceipt(runId);
    let ok=false;let output:Output={};
    try{output=await perform(selected,data);ok=true;}
    finally{
     const finished=await api('agentopia_owner_finish_skill_capability',{
      p_run_id:runId,p_success:ok,p_outcome:ok?'recorded_owner_capability':'capability_failed'
     });
     if(finished!==true)throw Error('Execution could not be recorded in the safety audit.');
    }
    setResult(output);
   }
  }catch(e){setError(e instanceof Error?e.message:'Capability could not complete.');}
  finally{setBusy(false);}
 }
 async function readFetch(){
  if(!fetchId||busy)return;
  setBusy(true);setError('');
  try{
   const response=dict(await api('agentopia_owner_skill_public_fetch_result',{p_fetch_id:fetchId}));
   if(response.pending){setResult({status:'Source request still processing.',fetchId});return;}
   if(response.ok!==true)throw Error(str(response.error||'Public data source unavailable.'));
   let content:unknown=response.data;
   // A public feed may be multi-megabyte: collect at most ten bounded chunks
   // from the *same* recorded GET, never start another network request.
   if(response.truncated===true){
    const pieces:string[]=[];
    let count=0;
    for(let i=0;i<10;i++){
     const part=dict(await api('agentopia_owner_skill_public_fetch_chunk',{
      p_fetch_id:fetchId,p_chunk:i
     }));
     if(part.ok!==true||typeof part.data!=='string')throw Error('Public source chunk missing or unavailable.');
     count=Number(part.chunkCount);
     if(!Number.isInteger(count)||count<1||count>10)throw Error('Public source exceeds bounded collection limit.');
     pieces.push(part.data);
     if(i+1===count)break;
    }
    if(pieces.length!==count)throw Error('Source was incomplete; no score was produced.');
    content=pieces.join('');
   }
   let normalized:Output;
   if(selected==='market_health_live'){
    if(typeof content!=='string')throw Error('Market breadth CSV not returned.');
    normalized=scoreMarketBreadth(content);
   }else if(selected==='market_uptrend_live'){
    if(typeof content!=='string')throw Error('Market uptrend CSV not returned.');
    normalized=scoreMarketUptrend(content);
   }else if(selected==='seo_html_live'){
    if(typeof content!=='string'||content.length<50||!/<html/i.test(content))
      throw Error('Public source did not return usable HTML.');
    // DOMParser creates an inert document. No fetched scripts run and no site writes occur.
    const parsed=new DOMParser().parseFromString(content,'text/html');
    const title=(parsed.title||'').trim();
    const desc=(parsed.querySelector('meta[name="description"]')?.getAttribute('content')||'').trim();
    const language=parsed.documentElement.getAttribute('lang')||'';
    const canon=parsed.querySelector('link[rel="canonical"]')?.getAttribute('href')||'';
    const viewport=parsed.querySelector('meta[name="viewport"]')?.getAttribute('content')||'';
    const headingNodes=[...parsed.querySelectorAll('h1,h2,h3,h4,h5,h6')].slice(0,150);
    const h1s=headingNodes.filter(x=>x.tagName.toLowerCase()==='h1');
    const images=[...parsed.querySelectorAll('img')].slice(0,500);
    const withoutAlt=images.filter(img=>!img.hasAttribute('alt')).length;
    const warnings=[
      !title?'Missing document title':title.length>65?'Document title exceeds 65 characters':null,
      !desc?'Missing meta description':desc.length>170?'Description exceeds 170 characters':null,
      !language?'Missing HTML language attribute':null,
      !viewport?'Missing viewport metadata':null,
      h1s.length!==1?'Expected one main H1 heading':null,
      withoutAlt>0?withoutAlt+' images have no alt attribute':null,
      !canon?'No canonical URL declared':null
    ].filter((x):x is string=>typeof x==='string');
    normalized={kind:'real_live_public_html_seo_audit',testedUrl:response.source,
      retrievedAt:response.fetchedAt,title,metaDescription:desc,lang:language,
      canonical:canon,viewport,h1Count:h1s.length,
      headings:headingNodes.map(h=>({tag:h.tagName.toLowerCase(),
        text:(h.textContent||'').trim().slice(0,140)})),
      imageCount:images.length,imagesMissingAlt:withoutAlt,
      internalLinkCount:[...parsed.querySelectorAll('a[href^="/"]')].length,
      structuredDataScripts:parsed.querySelectorAll('script[type="application/ld+json"]').length,
      warnings,warningCount:warnings.length,
      pagePerformanceMeasured:false,externalActions:false};
   }else if(['seo_live','site_lighthouse','competitor_site_audit'].includes(selected)){
    const doc=typeof content==='string'?JSON.parse(content):content;
    const report=dict(doc),lighthouse=dict(report.lighthouseResult);
    const categories=dict(lighthouse.categories),audits=dict(lighthouse.audits);
    if(!Object.keys(categories).length)throw Error('PageSpeed did not supply Lighthouse scores.');
    const scores=Object.fromEntries(Object.entries(categories)
      .filter(([,v])=>typeof dict(v).score==='number')
      .map(([key,v])=>[key,Math.round(Number(dict(v).score)*100)]));
    const findings=Object.entries(audits)
      .filter(([,v])=>typeof dict(v).score==='number'&&Number(dict(v).score)<0.9)
      .slice(0,45).map(([key,v])=>({id:key,title:str(dict(v).title),
       score:dict(v).score,description:str(dict(v).description).slice(0,330)}));
    normalized={source:'Google PageSpeed Insights',testedUrl:lighthouse.finalUrl||report.id,
      scores,findings,scannedAt:response.fetchedAt,
      categoriesChecked:Object.keys(scores).length,noWebsiteChanges:true};
   }else{
    normalized=normalizeFreePublicJson(selected,content);
   }
   setResult({data:normalized,source:response.source,fetchedAt:response.fetchedAt,operation:selected});
   setFetchId(null);
  }catch(e){setError(e instanceof Error?e.message:'Cannot retrieve source.');}
  finally{setBusy(false);}
 }
 const files=Array.isArray(result?.files)?result.files.filter(isFile):[];
 return <div className="ao-skill-capability-live" style={{marginTop:12,display:'grid',gap:8}}>
  <label style={{display:'grid',gap:4}}>Capability
   <select value={selected} disabled={busy||!available}
    onChange={e=>{
     const op=ops.find(o=>o.value===e.target.value);
     setSelected(e.target.value);setInputs(JSON.stringify(op?.example||{},null,2));
     setResult(null);setError('');setFetchId(null);setReceipt('');
    }}>
    {ops.map(x=><option key={x.value} value={x.value}>{x.label}</option>)}
   </select>
  </label>
  <label style={{display:'grid',gap:4}}>Data or instructions
   <textarea rows={5} value={inputs} maxLength={12000} disabled={busy||!available}
    spellCheck={false} onChange={e=>setInputs(e.target.value)}/>
  </label>
  <button type="button" disabled={!available||busy} onClick={()=>{void run()}}>
   <PlayCircle size={15}/> {busy?'Processing…':'Run capability'}
  </button>
  {fetchId&&<button type="button" disabled={busy} onClick={()=>{void readFetch()}}>
   <RefreshCw size={15}/> Check source result
  </button>}
  {receipt&&<small>Logged operation: {receipt.slice(0,14)}</small>}
  {error&&<p role="alert" style={{fontSize:12,overflowWrap:'anywhere'}}>{error}</p>}
  {!!files.length&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
   {files.slice(0,12).map((f,i)=><button type="button" key={i} onClick={()=>exportFile(f)}>
    <Download size={14}/>{f.name}</button>)}
  </div>}
  {result&&<pre role="status" style={{fontSize:12,maxHeight:300,overflow:'auto',
   whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{JSON.stringify(files.length?
     {files:files.map(f=>({name:f.name,bytes:f.content.length,format:f.mediaType}))}:result,null,2)?.slice(0,12000)}</pre>}
 </div>;
}
